import { Component, inject, ViewEncapsulation } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DialogSize,
  WcgButtonComponent,
  WcgDialogModule,
} from '@wcg/frontend-framework';
import { ERM_ICONS_ENUM } from '@erm/cluster';

import {
  ComposerAttachmentChip,
  ComposerAttachmentsDialogData,
} from '../../../types';

/**
 * ERM-12293 — scrollable list of every item linked on a message, opened
 * from "View More" once more than five chips are attached. Composer drafts
 * can remove chips; sent messages are read-only.
 */
@Component({
  host: { class: 'composer-attachments-dialog', 'data-v': 'next' },
  encapsulation: ViewEncapsulation.None,
  imports: [MatIcon, MatTooltip, WcgButtonComponent, WcgDialogModule],
  selector: 'wcg-collab-hub-composer-attachments-dialog',
  styleUrl: './composer-attachments-dialog.component.css',
  templateUrl: './composer-attachments-dialog.component.html',
})
export class ComposerAttachmentsDialogComponent {
  private readonly _dialogRef = inject(
    MatDialogRef<ComposerAttachmentsDialogComponent>,
  );
  private readonly _data =
    inject<ComposerAttachmentsDialogData>(MAT_DIALOG_DATA);

  readonly icons = ERM_ICONS_ENUM;
  readonly dialogSize = DialogSize.MEDIUM;

  /** Live chip list from the composer draft, or a static sent-message list. */
  readonly chips = this._data.chips;
  /** Composer disabled flag at open; sent messages leave this false. */
  readonly disabled = this._data.disabled ?? false;
  /** Sent messages cannot unlink items, so the remove control is omitted. */
  readonly canRemove = !!this._data.removeChip;

  /**
   * Opens the linked item and dismisses the dialog so the destination is
   * not covered by the modal. Missing and replaced documents stay put.
   */
  openLinkedItem(chip: ComposerAttachmentChip): void {
    if (this.disabled || chip.isMissing || chip.isReplaced) return;
    this._data.openChip(chip);
    this._dialogRef.close();
  }

  /**
   * Removes the chip from the draft. Closes when nothing remains to show.
   */
  removeLinkedItem(chip: ComposerAttachmentChip): void {
    if (this.disabled || !this._data.removeChip) return;
    this._data.removeChip(chip);
    if (this.chips().length === 0) this._dialogRef.close();
  }

  /** Dismisses the dialog without changing the draft. */
  close(): void {
    this._dialogRef.close();
  }
}
